<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

import { fetchMe } from '@/auth/api'
import { useAuth } from '@/auth/useAuth'
import MaintenanceToast from '@/maintenance/MaintenanceToast.vue'
import { useMaintenance } from '@/maintenance/useMaintenance'
import { useScrollbarWidth } from '@sapiendo/shared/sidebar/useScrollbarWidth'

// SD-3875 — expose `--scrollbar-width` en CSS variable globale pour aligner
// les bords droits des contrôles fixes vs zones scrollables (sidebar GGVie).
useScrollbarWidth()

const router = useRouter()
const auth = useAuth()

// SD-4253 — chien de garde de session unifié (30 s, identique ggvie/per). On
// revérifie la session côté serveur via `fetchMe()` (→ `authFetch`) : un access
// token expiré déclenche un refresh silencieux (session gardée vivante sans
// friction), et si le refresh échoue, `authFetch` émet `sessionExpired` → le
// router redirige vers /login (SOURCE UNIQUE de redirection, cf. router.ts).
// Filet pour le conseiller INACTIF qui ne déclenche aucune requête.
const SESSION_CHECK_INTERVAL_MS = 30_000
let heartbeat: ReturnType<typeof setInterval> | null = null

// SD-4367 — polling du statut maintenance (10 s, singleton) : alimente la toast
// de décompte et la bascule/restauration automatiques (cf. router.ts).
const maintenance = useMaintenance()

onMounted(() => {
  heartbeat = setInterval(() => {
    if (router.currentRoute.value.meta.public) return
    if (!auth.isAuthenticated.value) return
    void fetchMe().catch(() => undefined)
  }, SESSION_CHECK_INTERVAL_MS)
  maintenance.startPolling()
})
onUnmounted(() => {
  if (heartbeat) clearInterval(heartbeat)
  maintenance.stopPolling()
})

function onExitImpersonation(): void {
  const { restored, returnTo } = auth.exitImpersonation()
  if (!restored) return
  // Rechargement complet : les vues d'agent gardent en mémoire les jetons lus à leur chargement.
  window.location.assign(router.resolve(returnTo ?? '/admin/users').href)
}
</script>

<template>
  <div class="app-root">
    <div v-if="auth.isImpersonating.value && auth.user.value" class="impersonation-banner">
      <span class="imp-icon">👤</span>
      <span class="imp-text">
        Vous voyez la sandbox comme
        <strong>{{ auth.user.value.email }}</strong>
        (impersonation par {{ auth.impersonator.value?.email }})
      </span>
      <button class="imp-exit" @click="onExitImpersonation">Quitter l'impersonation</button>
    </div>
    <RouterView />
    <MaintenanceToast />
  </div>
</template>

<style>
*,
*::before,
*::after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html,
body {
  height: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue', sans-serif;
  font-size: 16px;
  line-height: 1.5;
  color: #111827;
  background-color: #f9fafb;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#app {
  height: 100%;
}

a {
  color: inherit;
  text-decoration: none;
}

button {
  cursor: pointer;
  border: none;
  background: none;
  font-family: inherit;
  font-size: inherit;
}

input {
  font-family: inherit;
  font-size: inherit;
}

/* SD-3875 — probe utilisé par useScrollbarWidth pour mesurer la scrollbar
 * custom de la sidebar GGVie. Doit avoir les MÊMES règles
 * `::-webkit-scrollbar` et `scrollbar-width` que `.conv-list`. */
.gg-sidebar-scrollbar-probe {
  scrollbar-width: thin;
}
.gg-sidebar-scrollbar-probe::-webkit-scrollbar {
  width: 8px;
}
</style>

<style scoped>
.app-root {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.impersonation-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 0.6rem 1rem;
  background: #fef3c7;
  color: #78350f;
  border-bottom: 2px solid #f59e0b;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.imp-icon {
  font-size: 1.1rem;
}

.imp-exit {
  padding: 0.35rem 0.85rem;
  background: #f59e0b;
  color: #fff;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.85rem;
}

.imp-exit:hover {
  background: #d97706;
}
</style>
